import React, { useState, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '@/components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  Upload, Sparkles, FileText, X, Check, AlertCircle, 
  Loader2, Trash2, ChevronDown, ChevronUp, Link as LinkIcon, UserPlus
} from 'lucide-react';
import CustomerFuzzyMatchDialog from './components/CustomerFuzzyMatchDialog';

export const BulkDOUpload = ({ open, onOpenChange, masterData, customers = [], onSuccess }) => {
  const { api } = useAuth();
  const fileInputRef = useRef(null);
  
  // Upload state
  const [files, setFiles] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [currentFileIndex, setCurrentFileIndex] = useState(-1);
  const [extractedData, setExtractedData] = useState([]);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [newCustomerNames, setNewCustomerNames] = useState([]);
  const [creating, setCreating] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());
  // iter 264d.15b — per-name fuzzy-match dialog (from the New Customers panel)
  const [fuzzyDialogOpen, setFuzzyDialogOpen] = useState(false);
  const [fuzzyMatches, setFuzzyMatches] = useState([]);
  const [fuzzyAttemptedName, setFuzzyAttemptedName] = useState('');
  // iter 264d.15b — bulk pre-flight duplicate review across ALL selected DOs
  const [preflightOpen, setPreflightOpen] = useState(false);
  const [preflightRows, setPreflightRows] = useState([]); // [{name, matches, decision}]
  const [preflightLoading, setPreflightLoading] = useState(false);

  // Handle file selection
  const handleFileSelect = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    const validFiles = selectedFiles.filter(file => {
      const isValid = file.type === 'application/pdf' || 
                      file.type.startsWith('image/') ||
                      file.name.toLowerCase().match(/\.(pdf|png|jpg|jpeg)$/);
      if (!isValid) {
        toast.error(`${file.name} is not a supported file type`);
      }
      return isValid;
    });
    
    setFiles(prev => [...prev, ...validFiles]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Remove a file from the list
  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
    setExtractedData(prev => prev.filter((_, i) => i !== index));
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      newSet.delete(index);
      // Adjust indices for items after the removed one
      const adjusted = new Set();
      newSet.forEach(i => {
        if (i > index) adjusted.add(i - 1);
        else adjusted.add(i);
      });
      return adjusted;
    });
  };

  // Process all files with AI
  const processFiles = async () => {
    if (files.length === 0) {
      toast.error('Please select files to process');
      return;
    }

    console.log('[BulkDO] Starting processFiles with', files.length, 'files');
    console.log('[BulkDO] API object exists:', !!api);
    
    setProcessing(true);
    setExtractedData([]);
    const results = [];

    for (let i = 0; i < files.length; i++) {
      setCurrentFileIndex(i);
      const file = files[i];
      
      console.log(`[BulkDO] Processing file ${i + 1}/${files.length}: ${file.name} (type: ${file.type}, size: ${file.size} bytes)`);
      
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        // Debug: Log FormData contents
        console.log('[BulkDO] FormData created, file appended:', file.name);
        
        console.log('[BulkDO] Sending POST to /ai/extract-do...');
        
        // Use explicit config to ensure proper FormData handling
        const response = await api.post('/ai/extract-do', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          timeout: 120000 // 2 minute timeout for AI processing
        });
        
        console.log('[BulkDO] Response received:', response.status, response.data);
        
        if (response.data.success) {
          const extractedResult = response.data.data;
          
          // Check if this DO has multiple containers
          if (extractedResult.has_multiple_containers && extractedResult.all_container_numbers?.length > 1) {
            // Expand into separate entries for each container
            extractedResult.all_container_numbers.forEach((containerNum, idx) => {
              results.push({
                filename: file.name,
                file: file,  // Store file reference for document upload
                status: 'success',
                data: {
                  ...extractedResult,
                  container_number: containerNum,
                  // Mark as part of multi-container DO
                  _isMultiContainer: true,
                  _containerIndex: idx + 1,
                  _totalContainers: extractedResult.all_container_numbers.length
                },
                error: null
              });
            });
          } else {
            // Single container - add as-is
            results.push({
              filename: file.name,
              file: file,  // Store file reference for document upload
              status: 'success',
              data: extractedResult,
              error: null
            });
          }
        } else {
          results.push({
            filename: file.name,
            file: file,
            status: 'error',
            data: null,
            error: response.data.error || 'Failed to extract data'
          });
        }
      } catch (error) {
        console.error('[BulkDO] Error processing file:', file.name);
        console.error('[BulkDO] Error type:', error.name);
        console.error('[BulkDO] Error message:', error.message);
        console.error('[BulkDO] Error response status:', error.response?.status);
        console.error('[BulkDO] Error response data:', error.response?.data);
        console.error('[BulkDO] Full error:', error);
        
        results.push({
          filename: file.name,
          file: file,
          status: 'error',
          data: null,
          error: error.response?.data?.detail || error.message || 'Processing failed'
        });
      }
    }

    console.log('[BulkDO] Processing complete. Results:', results.length, 'items');
    console.log('[BulkDO] Success count:', results.filter(r => r.status === 'success').length);
    console.log('[BulkDO] Error count:', results.filter(r => r.status === 'error').length);
    
    setExtractedData(results);
    setCurrentFileIndex(-1);
    setProcessing(false);
    
    // Auto-select successful extractions
    const successIndices = new Set();
    results.forEach((r, i) => {
      if (r.status === 'success') successIndices.add(i);
    });
    setSelectedItems(successIndices);
    
    // Count unique files and containers
    const successCount = results.filter(r => r.status === 'success').length;
    const multiContainerCount = results.filter(r => r.data?._isMultiContainer).length;
    
    if (multiContainerCount > 0) {
      toast.success(`Processed ${files.length} files: Found ${successCount} containers (${multiContainerCount} from multi-container DOs)`);
    } else {
      toast.success(`Processed ${files.length} files: ${successCount} successful`);
    }
    
    // Check for unmatched customers and show notification
    const unmatchedCustomers = new Set();
    results.forEach(r => {
      if (r.status === 'success' && r.data) {
        const custName = r.data.customer_name || '';
        // Check if this customer doesn't exist in the master customer list
        if (custName && !customers.some(c => 
          c.name?.toLowerCase().trim() === custName.toLowerCase().trim()
        )) {
          unmatchedCustomers.add(custName);
        }
      }
    });
    
    if (unmatchedCustomers.size > 0) {
      const names = Array.from(unmatchedCustomers).join(', ');
      toast.warning(
        `${unmatchedCustomers.size} customer(s) not found: ${names}. You can create them in Master Tables > Customers.`,
        { duration: 10000 }
      );
      setNewCustomerNames(Array.from(unmatchedCustomers));
    }
  };

  // Toggle item selection
  const toggleSelection = (index) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  // Toggle all selection
  const toggleSelectAll = () => {
    if (selectedItems.size === extractedData.filter(d => d.status === 'success').length) {
      setSelectedItems(new Set());
    } else {
      const allSuccess = new Set();
      extractedData.forEach((d, i) => {
        if (d.status === 'success') allSuccess.add(i);
      });
      setSelectedItems(allSuccess);
    }
  };

  // Toggle all containers from the same DO file
  const toggleSameDOSelection = (filename) => {
    const sameFileIndices = extractedData
      .map((item, index) => ({ item, index }))
      .filter(({ item }) => item.filename === filename && item.status === 'success')
      .map(({ index }) => index);
    
    // Check if all from this file are currently selected
    const allSelected = sameFileIndices.every(idx => selectedItems.has(idx));
    
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      sameFileIndices.forEach(idx => {
        if (allSelected) {
          newSet.delete(idx);
        } else {
          newSet.add(idx);
        }
      });
      return newSet;
    });
    
    toast.info(allSelected 
      ? `Deselected all containers from "${filename}"` 
      : `Selected all containers from "${filename}"`
    );
  };

  // Toggle row expansion
  const toggleExpand = (index) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  // Match master data
  const findMatchingId = (name, collection, nameField = 'name') => {
    if (!name || !collection) return null;
    const match = collection.find(item => 
      item[nameField]?.toLowerCase().includes(name.toLowerCase()) ||
      item.code?.toLowerCase().includes(name.toLowerCase())
    );
    return match?.id || null;
  };

  // iter 264d.15b — Create new customer from unmatched name, WITH fuzzy-match
  // pre-check to prevent SteamLogistics-style drift.
  const handleCreateCustomer = async (customerName) => {
    try {
      const fuzzy = await api.get(
        `/customers/fuzzy-match?name=${encodeURIComponent(customerName)}&threshold=0.85`
      );
      const matches = fuzzy.data?.matches || [];
      if (matches.length > 0) {
        setFuzzyAttemptedName(customerName);
        setFuzzyMatches(matches);
        setFuzzyDialogOpen(true);
        return; // wait for Link / Create-Anyway
      }
    } catch (_e) { /* non-fatal, proceed */ }
    await _doCreateCustomer(customerName);
  };

  const _doCreateCustomer = async (customerName) => {
    try {
      const response = await api.post('/admin/customers', {
        name: customerName,
        email: '',
        phone: '',
        address: ''
      });
      if (response.data) {
        toast.success(`Customer "${customerName}" created successfully`);
        setNewCustomerNames(prev => prev.filter(n => n !== customerName));
        setFuzzyDialogOpen(false);
        setFuzzyMatches([]);
        setFuzzyAttemptedName('');
        if (onSuccess) onSuccess();
      }
    } catch (error) {
      toast.error(`Failed to create customer: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleLinkExistingBulk = (existing) => {
    // Link the row in `extractedData` that had this customer name to the
    // existing customer, so createShipments() picks it up via customer_id.
    setExtractedData(prev => prev.map((row) => {
      if (!row?.extracted?.customer_name) return row;
      if (row.extracted.customer_name.trim().toLowerCase() !== fuzzyAttemptedName.trim().toLowerCase()) return row;
      return {
        ...row,
        extracted: {
          ...row.extracted,
          customer_name: existing.name,      // canonical name
          customer_id: existing.id,           // link
        },
      };
    }));
    setNewCustomerNames(prev => prev.filter(n => n !== fuzzyAttemptedName));
    toast.success(`Linked "${fuzzyAttemptedName}" to existing customer "${existing.name}"`);
    setFuzzyDialogOpen(false);
    setFuzzyMatches([]);
    setFuzzyAttemptedName('');
    if (onSuccess) onSuccess();
  };

  // iter 264d.15b — bulk pre-flight duplicate review. Called before
  // createShipments() when the user clicks "Review possible duplicates".
  // Groups all unique customer names in `extractedData`, fuzzy-matches each,
  // and lets the operator resolve them in one pass.
  const runDuplicatePreflight = async () => {
    setPreflightLoading(true);
    try {
      // Unique names from selected rows only.
      const names = new Set();
      extractedData.forEach((row, idx) => {
        if (!selectedItems.has(idx)) return;
        const n = row?.extracted?.customer_name?.trim();
        if (n && !row?.extracted?.customer_id) names.add(n);
      });

      const rows = [];
      for (const name of names) {
        try {
          const r = await api.get(`/customers/fuzzy-match?name=${encodeURIComponent(name)}&threshold=0.85`);
          const matches = r.data?.matches || [];
          if (matches.length > 0) {
            rows.push({ name, matches, decision: null }); // null = pending
          }
        } catch (_e) { /* skip on error */ }
      }

      if (rows.length === 0) {
        toast.success('No potential duplicates — safe to create.');
        return;
      }
      setPreflightRows(rows);
      setPreflightOpen(true);
    } finally {
      setPreflightLoading(false);
    }
  };

  const applyPreflightDecisions = () => {
    // For each resolved row where the operator chose "Link", rewrite the
    // matching extractedData items to point at the existing customer.
    setExtractedData(prev => prev.map((row) => {
      const cn = row?.extracted?.customer_name?.trim().toLowerCase();
      if (!cn) return row;
      const decided = preflightRows.find(
        (p) => p.name.trim().toLowerCase() === cn && p.decision && p.decision.type === 'link'
      );
      if (!decided) return row;
      return {
        ...row,
        extracted: {
          ...row.extracted,
          customer_name: decided.decision.customer.name,
          customer_id: decided.decision.customer.id,
        },
      };
    }));
    setNewCustomerNames((prev) => prev.filter((n) => {
      const linked = preflightRows.find(
        (p) => p.name.trim().toLowerCase() === n.trim().toLowerCase() && p.decision?.type === 'link'
      );
      return !linked;
    }));
    setPreflightOpen(false);
    setPreflightRows([]);
    const linkedCount = preflightRows.filter((p) => p.decision?.type === 'link').length;
    toast.success(linkedCount > 0
      ? `Resolved ${linkedCount} duplicate(s). Proceed to create shipments.`
      : 'All names kept as-is.');
  };

  // Create selected shipments
  const createShipments = async () => {
    if (selectedItems.size === 0) {
      toast.error('Please select at least one shipment to create');
      return;
    }

    setCreating(true);
    let successCount = 0;
    let errorCount = 0;
    let docUploadCount = 0;

    for (const index of selectedItems) {
      const item = extractedData[index];
      if (!item || item.status !== 'success') continue;

      const data = item.data;
      
      try {
        const payload = {
          container_number: data.container_number || `BULK${Date.now()}`,
          shipment_type: 'IMPORT',
          container_size: data.container_size || '',
          ssl_id: findMatchingId(data.ssl_name, masterData.ssls),
          terminal_id: findMatchingId(data.terminal_name, masterData.terminals),
          mbl: data.mbl || '',
          pin: data.pin || '',
          eta: data.eta || null,
          last_free_date: data.last_free_date || null,
          customer_id: findMatchingId(data.customer_name, customers),
          customer_name: data.customer_name || '',
          delivery_address: data.delivery_address || '',
          delivery_city: data.delivery_city || '',
          delivery_state: data.delivery_state || '',
          delivery_zip: data.delivery_zip || '',
          delivery_contact: data.delivery_contact || '',
          delivery_phone: data.delivery_phone || '',
          // Weight and overweight detection
          weight_lbs: data.weight_lbs || null,
          is_overweight: data.is_overweight || false,
          remarks: `${data.remarks || ''}${data.overweight_warning ? `\n⚠️ ${data.overweight_warning}` : ''}\n[Bulk imported from: ${item.filename}]`.trim()
        };

        const response = await api.post('/shipments', payload);
        const shipmentId = response.data.id;
        successCount++;
        
        // Upload the DO document to the newly created shipment
        if (item.file && shipmentId) {
          try {
            const docFormData = new FormData();
            docFormData.append('file', item.file);
            docFormData.append('document_type', 'DELIVERY_ORDER');
            
            await api.post(`/shipments/${shipmentId}/documents`, docFormData, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });
            docUploadCount++;
          } catch (docError) {
            console.warn(`Document upload failed for ${data.container_number}:`, docError);
            // Don't fail the whole operation if document upload fails
          }
        }
      } catch (error) {
        console.error(`Failed to create shipment for ${item.filename}:`, error);
        errorCount++;
      }
    }

    setCreating(false);

    if (successCount > 0) {
      let message = `Created ${successCount} shipment(s)`;
      if (docUploadCount > 0) {
        message += `, ${docUploadCount} document(s) attached`;
      }
      if (errorCount > 0) {
        message += `, ${errorCount} failed`;
      }
      toast.success(message);
      onSuccess?.();
      handleClose();
    } else {
      toast.error('Failed to create shipments');
    }
  };

  // Reset and close
  const handleClose = () => {
    setFiles([]);
    setExtractedData([]);
    setSelectedItems(new Set());
    setExpandedRows(new Set());
    setProcessing(false);
    setCreating(false);
    onOpenChange(false);
  };

  const progress = processing && files.length > 0 
    ? ((currentFileIndex + 1) / files.length) * 100 
    : 0;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] bg-slate-900 border-slate-800 flex flex-col" data-testid="bulk-do-upload-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-white flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-blue-400" />
            Bulk DO Upload
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Upload multiple Delivery Order documents and create shipments in bulk
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col gap-4">
          {/* File Upload Section */}
          <div className="p-4 bg-slate-800/50 rounded-lg border border-dashed border-slate-700">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept=".pdf,.png,.jpg,.jpeg"
              multiple
              className="hidden"
            />
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white font-medium mb-1">
                  {files.length === 0 ? 'Select DO Documents' : `${files.length} file(s) selected`}
                </p>
                <p className="text-slate-400 text-sm">
                  Supports PDF, PNG, JPG files
                </p>
              </div>
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={processing}
                variant="outline"
                className="border-blue-500/50 hover:border-blue-400"
                data-testid="add-files-btn"
              >
                <Upload className="h-4 w-4 mr-2" />
                Add Files
              </Button>
            </div>

            {/* File List */}
            {files.length > 0 && !extractedData.length && (
              <div className="mt-4 space-y-2 max-h-32 overflow-y-auto">
                {files.map((file, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-slate-900 rounded">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-slate-400" />
                      <span className="text-sm text-white">{file.name}</span>
                      <Badge variant="outline" className="text-xs">
                        {(file.size / 1024).toFixed(1)} KB
                      </Badge>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeFile(index)}
                      className="h-6 w-6 p-0 text-slate-400 hover:text-red-400"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {/* Progress Bar */}
            {processing && (
              <div className="mt-4 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-400">
                    Processing: {files[currentFileIndex]?.name || '...'}
                  </span>
                  <span className="text-white">
                    {currentFileIndex + 1} / {files.length}
                  </span>
                </div>
                <Progress value={progress} className="h-2" />
              </div>
            )}
          </div>

          {/* Unmatched Customers Alert */}
          {newCustomerNames.length > 0 && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3" data-testid="unmatched-customers-alert">
              <div className="flex items-center justify-between mb-2">
                <p className="text-amber-400 text-sm font-medium">
                  {newCustomerNames.length} customer(s) not found in your system:
                </p>
                {/* iter 264d.15b — bulk pre-flight duplicate review */}
                {selectedItems.size > 0 && (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={preflightLoading}
                    onClick={runDuplicatePreflight}
                    className="h-7 border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
                    data-testid="review-duplicates-btn"
                  >
                    {preflightLoading ? (
                      <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Scanning…</>
                    ) : (
                      <><LinkIcon className="h-3 w-3 mr-1" /> Review possible duplicates</>
                    )}
                  </Button>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {newCustomerNames.map((name) => (
                  <div key={name} className="flex items-center gap-1.5 bg-slate-800 rounded-md px-2 py-1">
                    <span className="text-slate-300 text-xs">{name}</span>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleCreateCustomer(name)}
                      className="h-5 px-1.5 text-xs text-amber-400 hover:text-amber-300 hover:bg-amber-500/20"
                      data-testid={`create-customer-${name.replace(/\s+/g, '-').toLowerCase()}`}
                    >
                      + Create
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Results Table */}
          {extractedData.length > 0 && (
            <ScrollArea className="flex-1 border border-slate-800 rounded-lg">
              <Table>
                <TableHeader className="sticky top-0 bg-slate-900 z-10">
                  <TableRow className="border-slate-800">
                    <TableHead className="w-10">
                      <Checkbox
                        checked={selectedItems.size === extractedData.filter(d => d.status === 'success').length && selectedItems.size > 0}
                        onCheckedChange={toggleSelectAll}
                        className="border-slate-600"
                      />
                    </TableHead>
                    <TableHead className="text-slate-400">Status</TableHead>
                    <TableHead className="text-slate-400">File</TableHead>
                    <TableHead className="text-slate-400">Container #</TableHead>
                    <TableHead className="text-slate-400">Customer</TableHead>
                    <TableHead className="text-slate-400">Delivery</TableHead>
                    <TableHead className="w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {extractedData.map((item, index) => (
                    <React.Fragment key={index}>
                      <TableRow 
                        className={`border-slate-800 ${item.status === 'error' ? 'opacity-60' : ''}`}
                      >
                        <TableCell>
                          <Checkbox
                            checked={selectedItems.has(index)}
                            onCheckedChange={() => toggleSelection(index)}
                            disabled={item.status === 'error'}
                            className="border-slate-600"
                          />
                        </TableCell>
                        <TableCell>
                          {item.status === 'success' ? (
                            <div className="flex items-center gap-1">
                              <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                                <Check className="h-3 w-3 mr-1" />
                                Success
                              </Badge>
                              {item.data?._isMultiContainer && (
                                <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30 text-xs">
                                  {item.data._containerIndex}/{item.data._totalContainers}
                                </Badge>
                              )}
                            </div>
                          ) : (
                            <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
                              <AlertCircle className="h-3 w-3 mr-1" />
                              Error
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-white text-sm">
                          <div className="flex flex-col">
                            <span className="font-mono">
                              {item.filename.length > 25 ? item.filename.slice(0, 25) + '...' : item.filename}
                            </span>
                            {item.data?._isMultiContainer && (
                              <span className="text-xs text-purple-400">
                                Container {item.data._containerIndex} of {item.data._totalContainers}
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className="text-white font-mono">
                              {item.data?.container_number || '-'}
                            </span>
                            {item.data?.is_overweight && (
                              <Badge className="bg-red-500 text-white text-xs px-1.5" title="Overweight Container">OW</Badge>
                            )}
                            {item.data?._isMultiContainer && item.data._containerIndex === 1 && (
                              <Badge variant="outline" className="text-xs text-purple-400 border-purple-500/30">
                                Multi-Container DO
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-slate-300">
                          {item.data?.customer_name || '-'}
                        </TableCell>
                        <TableCell className="text-slate-300 text-sm">
                          {item.data?.delivery_city && item.data?.delivery_state
                            ? `${item.data.delivery_city}, ${item.data.delivery_state}`
                            : '-'}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleExpand(index)}
                            className="h-6 w-6 p-0 text-slate-400"
                          >
                            {expandedRows.has(index) ? (
                              <ChevronUp className="h-4 w-4" />
                            ) : (
                              <ChevronDown className="h-4 w-4" />
                            )}
                          </Button>
                        </TableCell>
                      </TableRow>
                      {expandedRows.has(index) && (
                        <TableRow className="border-slate-800 bg-slate-800/30">
                          <TableCell colSpan={7} className="p-4">
                            {item.status === 'success' ? (
                              <div className="grid grid-cols-4 gap-4 text-sm">
                                {item.data?._isMultiContainer && (
                                  <div className="col-span-4 mb-2 p-2 bg-purple-500/10 border border-purple-500/30 rounded flex items-center justify-between">
                                    <div>
                                      <span className="text-purple-400 font-medium">
                                        Multi-Container DO: This is container {item.data._containerIndex} of {item.data._totalContainers} from "{item.filename}"
                                      </span>
                                      <div className="text-purple-300/70 text-xs mt-1">
                                        All containers: {item.data.all_container_numbers?.join(', ')}
                                      </div>
                                    </div>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="border-purple-500/50 text-purple-400 hover:bg-purple-500/20 whitespace-nowrap"
                                      onClick={() => toggleSameDOSelection(item.filename)}
                                    >
                                      Toggle All from DO
                                    </Button>
                                  </div>
                                )}
                                <div>
                                  <span className="text-slate-500">Size:</span>
                                  <span className="text-white ml-2">{item.data?.container_size || '-'}</span>
                                </div>
                                <div>
                                  <span className="text-slate-500">SSL:</span>
                                  <span className="text-white ml-2">{item.data?.ssl_name || '-'}</span>
                                </div>
                                <div>
                                  <span className="text-slate-500">Terminal:</span>
                                  <span className="text-white ml-2">{item.data?.terminal_name || '-'}</span>
                                </div>
                                <div>
                                  <span className="text-slate-500">MBL:</span>
                                  <span className="text-white ml-2">{item.data?.mbl || '-'}</span>
                                </div>
                                <div>
                                  <span className="text-slate-500">PIN:</span>
                                  <span className="text-white ml-2">{item.data?.pin || '-'}</span>
                                </div>
                                <div>
                                  <span className="text-slate-500">ETA:</span>
                                  <span className="text-white ml-2">{item.data?.eta || '-'}</span>
                                </div>
                                <div>
                                  <span className="text-slate-500">LFD:</span>
                                  <span className="text-white ml-2">{item.data?.last_free_date || '-'}</span>
                                </div>
                                <div>
                                  <span className="text-slate-500">Contact:</span>
                                  <span className="text-white ml-2">{item.data?.delivery_contact || '-'}</span>
                                </div>
                                <div className="col-span-4">
                                  <span className="text-slate-500">Address:</span>
                                  <span className="text-white ml-2">
                                    {[item.data?.delivery_address, item.data?.delivery_city, item.data?.delivery_state, item.data?.delivery_zip]
                                      .filter(Boolean).join(', ') || '-'}
                                  </span>
                                </div>
                                {item.data?.remarks && (
                                  <div className="col-span-4">
                                    <span className="text-slate-500">Remarks:</span>
                                    <span className="text-white ml-2">{item.data.remarks}</span>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="text-red-400">
                                Error: {item.error}
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>
          )}
        </div>

        <DialogFooter className="border-t border-slate-800 pt-4">
          <div className="flex items-center justify-between w-full">
            <div className="text-sm text-slate-400">
              {extractedData.length > 0 && (
                <>
                  {selectedItems.size} of {extractedData.filter(d => d.status === 'success').length} selected
                </>
              )}
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={handleClose}
                className="border-slate-700"
              >
                Cancel
              </Button>
              {extractedData.length === 0 ? (
                <Button
                  onClick={processFiles}
                  disabled={files.length === 0 || processing}
                  className="bg-blue-600 hover:bg-blue-500"
                  data-testid="process-files-btn"
                >
                  {processing ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4 mr-2" />
                      Process {files.length} File{files.length !== 1 ? 's' : ''}
                    </>
                  )}
                </Button>
              ) : (
                <Button
                  onClick={createShipments}
                  disabled={selectedItems.size === 0 || creating}
                  className="bg-green-600 hover:bg-green-500"
                  data-testid="create-shipments-btn"
                >
                  {creating ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Check className="h-4 w-4 mr-2" />
                      Create {selectedItems.size} Shipment{selectedItems.size !== 1 ? 's' : ''}
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </DialogFooter>
      </DialogContent>

      {/* iter 264d.15b — per-name fuzzy-match dialog */}
      <CustomerFuzzyMatchDialog
        open={fuzzyDialogOpen}
        onOpenChange={setFuzzyDialogOpen}
        attemptedName={fuzzyAttemptedName}
        matches={fuzzyMatches}
        onLink={handleLinkExistingBulk}
        onCreateAnyway={() => _doCreateCustomer(fuzzyAttemptedName)}
      />

      {/* iter 264d.15b — bulk pre-flight duplicate review */}
      <Dialog open={preflightOpen} onOpenChange={setPreflightOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-2xl" data-testid="preflight-dupes-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-amber-400" />
              Review possible duplicate customers
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              These names look similar to existing customers. Pick "Link" to reuse an existing entity, or leave as "Create new".
              Applies only to the {selectedItems.size} selected shipment(s).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
            {preflightRows.map((row, ri) => (
              <div key={row.name} className="rounded-lg border border-slate-800 bg-slate-950 p-3" data-testid={`preflight-row-${ri}`}>
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div>
                    <div className="text-white text-sm font-medium">
                      Extracted: <span className="font-mono">&quot;{row.name}&quot;</span>
                    </div>
                    <div className="text-slate-500 text-xs mt-0.5">
                      {row.matches.length} possible match(es)
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant={row.decision?.type === 'create' ? 'default' : 'outline'}
                    className={row.decision?.type === 'create' ? 'bg-amber-600 hover:bg-amber-500 text-white' : 'border-amber-500/40 text-amber-300 hover:bg-amber-500/10'}
                    onClick={() => setPreflightRows((rows) => rows.map((r, i) => i === ri ? { ...r, decision: { type: 'create' } } : r))}
                    data-testid={`preflight-create-${ri}`}
                  >
                    <UserPlus className="h-3 w-3 mr-1" />
                    Create new
                  </Button>
                </div>
                <div className="space-y-1.5">
                  {row.matches.map((m) => {
                    const pct = Math.round((m.similarity || 0) * 100);
                    const isPicked = row.decision?.type === 'link' && row.decision?.customer?.id === m.id;
                    return (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() => setPreflightRows((rows) => rows.map((r, i) => i === ri ? { ...r, decision: { type: 'link', customer: m } } : r))}
                        className={
                          'w-full text-left rounded border p-2 flex items-center justify-between transition-colors ' +
                          (isPicked ? 'border-blue-500 bg-blue-500/10' : 'border-slate-800 hover:border-slate-600')
                        }
                        data-testid={`preflight-link-${ri}-${m.id}`}
                      >
                        <div>
                          <div className="text-white text-sm">{m.name}</div>
                          <div className="text-slate-500 text-xs">{m.code ? `Code ${m.code}` : (m.email || '')}</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={pct >= 95 ? 'bg-emerald-500/20 text-emerald-300' : pct >= 90 ? 'bg-blue-500/20 text-blue-300' : 'bg-amber-500/20 text-amber-300'}>
                            {pct}% match
                          </Badge>
                          {isPicked && <Check className="h-4 w-4 text-blue-400" />}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          <DialogFooter className="border-t border-slate-800 pt-3">
            <Button variant="outline" onClick={() => setPreflightOpen(false)} data-testid="preflight-cancel-btn">Cancel</Button>
            <Button
              className="bg-blue-600 hover:bg-blue-500"
              onClick={applyPreflightDecisions}
              disabled={preflightRows.some((r) => !r.decision)}
              data-testid="preflight-apply-btn"
            >
              Apply decisions
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default BulkDOUpload;
